import { createContext, useContext, useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import enDefault from "../locales/en.json";
import kaDefault from "../locales/ka.json";
import LanguageChooserModal from "../components/LanguageChooserModal";

const SUPPORTED = ["en", "ka"];
const LanguageContext = createContext();

const applyFont = (lang) => {
  const font =
    lang === "ka" ? "'Noto Sans Georgian', sans-serif" : "'Inter', sans-serif";
  const all = document.querySelectorAll("*");
  all.forEach((el) => {
    if (!el.classList.contains("material-symbols-outlined")) {
      el.style.fontFamily = font;
    }
  });
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    if (saved) {
      document.documentElement.lang = saved;
      setTimeout(() => applyFont(saved), 200);
      return saved;
    }
    // Auto-detect browser language for first-time visitors
    const browser = navigator.language || navigator.userLanguage || "en";
    const detected = browser.startsWith("ka") ? "ka" : "en";
    localStorage.setItem("language", detected);
    document.documentElement.lang = detected;
    setTimeout(() => applyFont(detected), 200);
    return detected;
  });

  const [translations, setTranslations] = useState({
    en: enDefault,
    ka: kaDefault,
  });

  const [showLanguageChooser, setShowLanguageChooser] = useState(
    () => !localStorage.getItem("languageConfirmed"),
  );

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const snap = await getDoc(doc(db, "siteSettings", "translations"));
        if (snap.exists()) {
          const data = snap.data();
          setTranslations({
            en: { ...enDefault, ...data.en },
            ka: { ...kaDefault, ...data.ka },
          });
        }
      } catch (err) {
        console.error("Error loading translations:", err);
      }
    };
    loadTranslations();
  }, []);

  const t = (key) =>
    translations[language][key] || translations["en"][key] || key;

  const switchLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
    document.documentElement.lang = lang;
    setTimeout(() => applyFont(lang), 50);
  };

  const chooseLanguage = (lang) => {
    switchLanguage(lang);
    localStorage.setItem("languageConfirmed", "true");
    setShowLanguageChooser(false);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        switchLanguage,
        t,
        SUPPORTED,
        translations,
        setTranslations,
      }}
    >
      {children}
      {showLanguageChooser && <LanguageChooserModal onChoose={chooseLanguage} />}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
